import { useEffect, useRef } from 'react'
import { prettyNum, useWindowContext, Page, noop } from '@iqueue/ui-kit'
import useSocket from '../../hooks/useSocket'
import moment from 'moment'
import ErrorView from '../../components/errors/ErrorView'
import EventPresenter from '../../api/eventPresenter'
import useTranslate, { t } from '../../hooks/useTranslate'

function col(value, len, dir = 'left') {
  if (value.length > len) {
    value = value.substring(0, len - 5) + '...  '
  } else if (value.length < len) {
    if (dir !== 'left') value += dir
    const space = Array.from({ length: len - value.length }).fill(' ').join('')
    value = dir === 'left' ? value + space : space + value
  }

  return value
}

const HEADER = [
  col('    Date', 29),
  col('Gateway', 18),
  col('Device EUI', 18),
  col('Data', 66),
  col('Value', 20),
  col('Frequency', 13),
  col('fCnt', 8, '  '),
  col('Port', 6, '  '),
  col('DR', 4, '  '),
  col('RSSI', 6, '  '),
  col('SNR', 5, ''),
].join('')

function zeroLeak(v) {
  return ((v + '').length === 1 ? '0' : '') + v
}

function interpolateColor(val, min, max) {
  if (val === null) return '??'

  const f = Math.min(1, Math.max(0, (val - min) / (max - min)))

  let c0 = '58AC30'
  let c1 = 'F44336'

  if (!isFinite(val)) {
    return '#' + c1
  }

  c0 = c0.match(/.{1,2}/g).map((oct) => parseInt(oct, 16) * (1 - f))
  c1 = c1.match(/.{1,2}/g).map((oct) => parseInt(oct, 16) * f)
  // noinspection JSCheckFunctionSignatures
  let ci = [0, 1, 2].map(i => Math.min(Math.round(c0[i] + c1[i]), 255))
  return '#' + ci.reduce((a, v) => ((a << 8) + v), 0).toString(16).padStart(6, "0")
}

function pn(val, fixed = 0) {
  if (val === null) return '??'

  return prettyNum(val, 10, {
    delimiter: ' ',
    separator: '.',
    fixed,
  })
}

function parseData(val) {
  if (val === null) return '??'

  val = atob(val)
  let out = ''

  for (let i = 0; i < val.length; i++) {
    out += zeroLeak(val.charCodeAt(i).toString(16).toUpperCase())
  }

  return out
}

function prettyFreq(val) {
  if (val === null) return '??'

  val = (val / 1000000).toFixed(5).replace(/0+$/g, '')
  if (val.endsWith('.')) val += '0'

  return val
}

function compressMessage({ a, c, d, e, f, i, r, s, t, p, v }) {
  const line = [
    col(t === null ? '??' : moment(t).format('DD.MM.YYYY HH:mm:ss.SSS'), 25),
    col(i === null ? '??' : (i || '').toUpperCase(), 18),
    col(e === null ? '??' : (e || '').toUpperCase(), 18),
    col(parseData(d), 66),
    col(v === null ? ' ' : (v.join(', ')), 20),
    col(prettyFreq(f), 13),
    col(pn(c), 8, '  '),
    col(p === null ? '??' : p + '', 6, '  '),
  ].join('')

  const dir = `<mark style="background:var(--iq-color);color:var(--iq-background);">RX</mark>  ` // TX
  const dr = `<mark style="background: ${ interpolateColor(a, 0, 15) }">${ col(a === null ? '??' : a + '', 2, '') }</mark>  `
  const rssi = `<mark style="background: ${ interpolateColor(r, -30, -120) }">${ col(r === null ? '??' : pn(r), 4, '') }</mark>  `
  const snr = `<mark style="background: ${ interpolateColor(s, 10, -20) }">${ col(s === null ? '??' : pn(s, 1), 5, '') }</mark>`

  return dir + line + dr + rssi + snr
}

function LogsView() {
  const socket = useSocket()
  const logsRef = useRef()

  useEffect(() => {
    EventPresenter.buffer().then((entries) => {
      let lines = ''

      entries.forEach((message) => {
        lines += (lines === '' ? '' : '\n') + compressMessage(message)
      })

      logsRef.current.innerHTML = lines
    }).catch(noop)

    const listener = (message) => {
      logsRef.current.innerHTML = compressMessage(message) + '\n' + logsRef.current.innerHTML
    }
    socket.on(`events/encoded`, listener)
    return () => {
      socket.off(`events/encoded`, listener)
    }
  }, [socket])

  return <>
    <code className={ 'sensor-logs-view header' }>
      <pre>{ HEADER }</pre>
    </code>

    <code className={ 'sensor-logs-view' }>
      <pre ref={ logsRef }/>
    </code>
  </>
}

export default function LogsPage() {
  const { togglePaddings, setTitle, toggleScrollbars } = useWindowContext()
  const locale = useTranslate()

  useEffect(() => {
    noop(locale)
    setTitle(t('menu.logs'))
    togglePaddings(false)
    toggleScrollbars(true)
  }, [locale])

  return <Page
    component={ LogsView }
    errorView={ ErrorView }
  />
}
